// Ядро кор-лупа + система уровней: маятник → падение → посадка
// (перфект / срез / соскальзывание), комбо, жизни, очки, качание башни,
// ветер, фишки зданий (ярусы, сдвиги, закрутка, шпили), камера.
// Все цифры баланса — из /api/config (config/levels.json).
import * as THREE from 'three';
import { createBlock, createDebris, createSpire, disposeMesh, createContactShadow } from './block.mjs';
import { createTower } from './tower.mjs';
import { createFx } from './fx.mjs';
import { applyTheme } from './env.mjs';
import { followShadow } from './scene.mjs';
import { createSpring, shakeOffset } from './anim.mjs';
import { sfx, unlockAudio, music } from './audio.mjs';
import { foremanLevel, FOREMAN } from '../../lib/stars.js';
import { createCrowd } from './crowd.mjs';
import { showBlueprint, blueprintSeen } from './blueprint.mjs';
import * as phys from './physics.mjs';

export function createGame({ scene, camera, crane, hud, lights, config, startLevel = 0, onWin, onLevelStart, onLevelComplete, getGift, claimGift, onShare, onMap, getBonusLives, haptic, achEvent, getBestScore, getRunStars, getCrowdScale }) {
  const ach = achEvent || (() => {});
  const physics = config.physics;
  const scoring = physics.scoring;
  const effects = config.effects || {}; // wow-эффекты: каждый флаг отключаем в levels.json

  const tower = createTower(scene, physics);
  const fx = createFx(scene);
  const crowd = createCrowd(scene);
  let hitStopTimer = 0;   // заморозка мира на перфекте (hit-stop)
  let bulletTime = false; // замедление падения последнего блока

  // --- Текущий уровень ---
  let levelIndex = -1;
  let level = null;
  let P = null; // level.pendulum
  let H = 1.1;
  let D = 3.2;
  let env = null; // окружение темы
  let spireMesh = null;
  let pixelDir = 1; // чередование стороны сценарных сдвигов

  // --- Состояние партии ---
  let state = 'swing'; // swing | fall | settle | slip | win | lose
  let block = null;
  let blockWidth = 3.2;
  let swingPhase = 0;
  let swingVx = 0; // аналитическая скорость блока на маятнике (не зависит от FPS)
  const vel = new THREE.Vector2();
  let lives = physics.lives;
  let score = 0;
  let combo = 0;
  let maxCombo = 0;
  let perfects = 0;
  let settleTimer = 0;
  let slipSpin = 0;
  let shake = 0;
  let pullTimer = 0; // отъезд камеры раз в 10 этажей
  let taps = 0;      // счётчик бросков — уходит на сервер для античита
  let paused = false;
  let maxLives = physics.lives;

  // --- Анимационные пружины (B1, B2) ---
  const squash = createSpring(1, { stiffness: 220 });   // масштаб блока по Y
  const camY = createSpring(6, { stiffness: 42 });      // высота камеры
  const camZ = createSpring(16, { stiffness: 42 });     // дистанция
  const fovKick = createSpring(0, { stiffness: 150 });  // добавка к FOV
  const baseFov = camera.fov;
  let shakeAmount = 0;
  let contactShadow = null;
  let flyby = -1;               // прогресс круга почёта 0..1, -1 = не идёт
  const FLYBY_SECONDS = 3.6;
  let photoMode = false;        // фото-режим: медленная орбита вокруг здания
  let photoAngle = 0;

  // --- Золотой блок ×5 (секция E) ---
  // Каждый 7-й этаж кран привозит золотой блок: посади его — очки посадки ×5.
  // Номер детерминирован, а не случаен: сервер должен уметь посчитать
  // честный потолок очков, с рандомом античит невозможен.
  const GOLDEN_EVERY = 7;
  let bonusBlock = false;

  // --- Призрак рекорда (секция E) ---
  const BEST_KEY = 'etazhi_tower_bestfloors';
  const readBest = () => { try { return JSON.parse(localStorage.getItem(BEST_KEY) || '{}'); } catch (e) { return {}; } };
  let ghostLine = null;      // полупрозрачная отметка лучшей высоты
  let bestFloors = 0;
  let recordBeaten = false;

  function saveBestFloors() {
    if (level.daily) return; // у Башни дня свой серверный зачёт
    const all = readBest();
    if (tower.count > (all[level.id] || 0)) {
      all[level.id] = tower.count;
      localStorage.setItem(BEST_KEY, JSON.stringify(all));
    }
  }

  function makeGhostLine(floors) {
    const g = new THREE.Group();
    const bar = new THREE.Mesh(
      new THREE.BoxGeometry(level.blockWidth + 2.2, 0.07, D + 2.2),
      new THREE.MeshBasicMaterial({ color: 0xe2231a, transparent: true, opacity: 0.38 })
    );
    g.add(bar);
    // Табличка «РЕКОРД · N эт.» на канвасе
    const c = document.createElement('canvas');
    c.width = 256; c.height = 64;
    const ctx = c.getContext('2d');
    ctx.fillStyle = 'rgba(226,35,26,.92)';
    ctx.fillRect(0, 0, 256, 64);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 30px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`РЕКОРД · ${floors} эт.`, 128, 34);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Mesh(
      new THREE.PlaneGeometry(2.6, 0.65),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.9 })
    );
    label.position.set(level.blockWidth / 2 + 2.6, 0.45, 0);
    g.add(label);
    g.position.y = 0.5 + floors * H;
    return g;
  }

  // Вехи высоты (игровые метры): узнаваемые сравнения вместо голых цифр
  const MILESTONES = [
    [15, 'выше хрущёвки'],
    [30, 'высота панельки'],
    [48, 'выше Биг-Бена? Почти!'],
    [75, 'три четверти МГУ'],
    [105, 'выше главного здания МГУ'],
    [150, 'половина Эйфелевой башни'],
    [200, 'выше небоскрёба «Высоцкий»'],
    [250, 'почти Эйфелева башня'],
    [300, 'ровно Эйфелева башня!'],
  ];
  let milestoneIdx = 0;

  // --- Ветер ---
  let windValue = 0;
  let windTarget = 0;
  let windTimer = 0;

  /** Обломки среза: летят с физикой и тают */
  const debris = [];
  /** Блок, который сейчас отыгрывает посадку пружиной */
  let settlingBlock = null;

  const camBaseX = 8;
  // Дистанция зависит от пропорций окна и должна их переживать: в Telegram
  // высота меняется на expand и повороте, иначе кадр остаётся от старого аспекта
  const camDistFor = () => ((window.innerWidth / window.innerHeight) < 0.8 ? 24 : 16);
  let camDist = camDistFor();
  window.addEventListener('resize', () => { camDist = camDistFor(); });

  function pivot() {
    return { x: crane.trolleyX, y: crane.jibY - 0.15 + crane.group.position.y };
  }

  // Математика маятника/посадки — в physics.mjs (чистые функции + тесты)
  function swingSpeed() {
    return phys.swingSpeed(P, tower.count);
  }

  function profileWidth(floorIndex) {
    return phys.profileWidth(level, floorIndex);
  }

  function windActive() {
    return level.wind && tower.count >= level.wind.fromFloor && level.wind.maxForce > 0;
  }

  function spawnBlock() {
    const top = tower.topInfo(level.blockWidth);
    // Новый блок: не шире верхнего и не шире яруса здания
    blockWidth = Math.min(top.width, profileWidth(tower.count));
    // Золотой блок виден уже в кране — игрок понимает, что этот ронять нельзя
    bonusBlock = phys.isGoldenFloor(tower.count + 1, level.floors, GOLDEN_EVERY);
    block = createBlock(blockWidth, H, D, bonusBlock ? 'golden' : 'dark', level.building);
    if (bonusBlock) hud.announce('Золотой блок!', 'посади его — очки ×5');
    const p = pivot();
    block.position.set(p.x, p.y - P.cable, 0);
    scene.add(block);
    // Верёвку выпрямляем под новый блок, иначе она «прилетит» из прошлой позиции
    crane.resetRope(new THREE.Vector3(block.position.x, block.position.y + H / 2, 0));
    swingPhase = 0;
    state = 'swing';
    // Последний блок уровня: обрыв музыки — тишина, удар, фанфары
    if (tower.count === level.floors - 1) music.dropout();
    hud.hint(levelIndex === 0 && tower.count === 0);
  }

  function replaceBlockMesh(width, variant, x, y) {
    scene.remove(block);
    disposeMesh(block);
    block = createBlock(width, H, D, variant, level.building);
    block.position.set(x, y, 0);
    return block;
  }

  function addScore(points) {
    const mult = combo >= scoring.comboMultiplierFrom ? scoring.comboMultiplier : 1;
    score += points * mult * (level.foreman ? FOREMAN.scoreMultiplier : 1);
    hud.setScore(score);
  }

  function spawnDebris(width, x, y, dir) {
    const m = createDebris(width, H, D);
    m.position.set(x, y, 0);
    scene.add(m);
    debris.push({ mesh: m, vx: dir * (1.5 + Math.random()), vy: 1.2, spin: dir * (2 + Math.random() * 2), life: 1.6 });
  }

  // --- Разбор посадки (пороговая математика — physics.classifyLanding) ---
  function resolveLanding() {
    const top = tower.topInfo(level.blockWidth);
    const verdict = phys.classifyLanding({
      bx: block.position.x,
      blockWidth,
      topX: top.x,
      topWidth: top.width,
      // «Прораб»: зона перфекта уже на 30% — сложность в обмен на очки ×2
      perfectThreshold: physics.perfectThreshold * (level.foreman ? FOREMAN.perfectScale : 1),
      slipThreshold: physics.slipThreshold,
    });

    if (verdict.type === 'perfect') landPerfect(top);
    else if (verdict.type === 'cut') landCut(top, verdict.offset, verdict.left, verdict.right);
    else landSlip(verdict.offset);
  }

  function landPerfect(top) {
    combo += 1;
    maxCombo = Math.max(maxCombo, combo);
    perfects += 1;

    // Золотой ЭТАЖ (награда за комбо 5) и золотой БЛОК (×5 очков) — разные
    // события: визуально оба золотые, но бонусы не складываются в кучу
    const goldenCombo = combo > 0 && combo % 5 === 0;
    const variant = (goldenCombo || bonusBlock) ? 'golden' : combo >= 3 ? 'people' : 'lit';
    replaceBlockMesh(blockWidth, variant, top.x, top.y + H / 2);
    settleBlock(top.x, blockWidth);

    addScore(scoring.perfect * (bonusBlock ? 5 : 1));
    ach('perfect', { combo });
    if (bonusBlock) ach('golden');
    tower.calm();
    sfx.perfect(combo);
    if (haptic) haptic('success');
    crane.setMood(1, 1.4);          // кран радуется
    fovKick.kick(-38);              // короткий наезд камеры на перфекте
    fx.perfectBurst(block.position.clone());
    // Hit-stop: 60мс заморозки + радиальная волна — «сочность» файтингов
    if (effects.hitStop) {
      hitStopTimer = 0.06;
      fx.shockwave(block.position.clone());
    }
    hud.perfectPop(combo >= 3 ? `PERFECT ×${combo}!` : 'PERFECT!');
    hud.combo(combo, combo >= scoring.comboMultiplierFrom);

    if (goldenCombo) {
      addScore(scoring.goldenFloorBonus);
      fx.celebration(block.position.clone());
      sfx.golden();
      hud.perfectPop('ЗОЛОТОЙ ЭТАЖ!');
    }
    if (bonusBlock) {
      fx.celebration(block.position.clone());
      sfx.golden();
      hud.perfectPop('ЗОЛОТОЙ ×5!');
    }
    if (combo === 10) {
      addScore(scoring.helicopterBonus);
      fx.helicopterFly(top.y);
      sfx.helicopter();
    }
    shake = 0.12;
  }

  function landCut(top, offset, left, right) {
    combo = 0;
    hud.combo(0, false);

    const newWidth = right - left;
    const newX = (left + right) / 2;
    const cutWidth = blockWidth - newWidth;
    const dir = Math.sign(offset);
    spawnDebris(cutWidth, newX + dir * (newWidth / 2 + cutWidth / 2), top.y + H / 2, dir);

    // Золотой блок остаётся золотым и после среза — награда за спасённую посадку
    replaceBlockMesh(newWidth, bonusBlock ? 'golden' : 'dark', newX, top.y + H / 2);
    blockWidth = newWidth;
    settleBlock(newX, newWidth);

    const precision = 1 - Math.abs(offset) / (physics.slipThreshold * (newWidth + cutWidth));
    const landPoints = Math.round(scoring.landingBase + scoring.landingPrecisionBonus * Math.max(0, precision));
    addScore(landPoints * (bonusBlock ? 5 : 1));
    if (bonusBlock) { hud.perfectPop('ЗОЛОТОЙ ×5!'); ach('golden'); }

    tower.impulse(offset);
    sfx.cut(); // хруст среза поверх удара посадки
    if (haptic) haptic('impact');
    fx.dust(block.position.clone(), newWidth);
    shakeAmount = 0.16;
  }

  function landSlip(offset) {
    combo = 0;
    hud.combo(0, false);
    lives -= 1;
    hud.setLives(lives, maxLives);
    hud.flashRed();
    sfx.crash();
    if (haptic) haptic('heavy');
    crane.setMood(-1, 2);          // кран грустит
    tower.impulse(offset * 1.6);

    state = 'slip';
    vel.x = Math.sign(offset) * 3;
    slipSpin = -Math.sign(offset) * 3.5;
    shakeAmount = 0.42;
  }

  // Блок сел: сплющивание, запись в башню, фишки уровня, проверка победы
  function settleBlock(baseX, width) {
    // Закрутка (Лахта, Шанхай): каждый этаж повёрнут чуть сильнее
    if (level.twistPerFloor) block.rotation.y = tower.count * level.twistPerFloor;

    // Squash & stretch: удар по пружине, она сама вернёт форму с «дожимом»
    squash.reset(0.88);
    squash.setTarget(1);
    squash.kick(-1.2);
    settlingBlock = block;
    tower.addBlock(block, baseX, width, H);

    // Пиксельные вырезы MahaNakhon: сценарный сдвиг только что севшего этажа
    if (level.pixelShift && level.pixelShift.floors.includes(tower.count)) {
      tower.shiftTop(pixelDir * level.pixelShift.amount);
      pixelDir = -pixelDir;
    }

    state = 'settle';
    settleTimer = 0.28;

    hud.setFloors(tower.count, level.floors);
    hud.setMeters(tower.count * physics.floorMeters);
    ach('floor');

    // Музыка живёт с игрой: слои от прогресса, темп — за маятником
    music.setProgress(tower.count, combo);
    music.setRate(swingSpeed() / P.speedStart);

    // Призрак рекорда побеждён: анонс один раз, отметка гаснет.
    // Сохраняем сразу — сворачивание аппа не должно съесть рекорд.
    if (bestFloors > 0 && !recordBeaten && tower.count > bestFloors) {
      recordBeaten = true;
      hud.announce('Новый рекорд!', `прошлый — ${bestFloors} эт.`);
      ach('record');
      if (haptic) haptic('success');
      if (ghostLine) ghostLine.visible = false;
    }
    if (tower.count > bestFloors) saveBestFloors();

    // Высотные вехи: сравнение с реальными зданиями даёт масштабу смысл.
    // Показ с задержкой, чтобы не столкнуться с PERFECT-попом момента посадки;
    // на финальном этаже не показываем — там сразу круг почёта.
    const meters = tower.count * physics.floorMeters;
    while (milestoneIdx < MILESTONES.length && meters >= MILESTONES[milestoneIdx][0]) {
      const [m, label] = MILESTONES[milestoneIdx];
      milestoneIdx++;
      if (tower.count < level.floors) {
        setTimeout(() => { if (state !== 'win' && state !== 'lose') hud.announce(`${m} метров!`, label); }, 650);
      }
    }

    if (tower.count > 0 && tower.count % 10 === 0 && tower.count < level.floors) {
      pullTimer = 2.2; // момент гордости: показать башню целиком
      sfx.checkpoint();
      // Стройка собирает зевак: каждые 10 этажей толпа внизу прибывает
      if (effects.crowd) {
        crowd.setScale(getCrowdScale ? getCrowdScale() : 1);
        crowd.add(4);
      }
    }
  }

  function finishWin() {
    state = 'win';
    hud.hint(false);
    const top = tower.topInfo(level.blockWidth);

    // Шпиль здания — финальный штрих
    if (level.spire) {
      spireMesh = createSpire(level.spire);
      spireMesh.position.set(top.x, top.y, 0);
      scene.add(spireMesh);
    }

    fx.celebration(new THREE.Vector3(top.x, top.y + 1, 0));
    sfx.fanfare();
    if (effects.crowd) {
      crowd.celebrate(true); // зеваки прыгают и подбрасывают шапки
      sfx.applause();
    }
    ach('win', {
      levelId: level.id,
      floors: tower.count,
      perfects,
      livesLost: maxLives - lives,
      livesLeft: lives,
    });
    // Победа в Башне дня не двигает прогресс кампании
    if (!level.daily && onWin) onWin(levelIndex);
    if (onLevelComplete) {
      onLevelComplete(level.id, { score, floors: tower.count, perfects, taps, daily: !!level.daily, foreman: !!level.foreman });
    }

    // Круг почёта: камера обходит здание, снизу вверх идёт волна окон.
    // Итоги показываем после облёта — или сразу, если игрок тапнул.
    flyby = 0;
  }

  function showWinOverlay() {
    const isDaily = !!level.daily;
    const isForeman = !!level.foreman;
    const hasNext = !isDaily && !isForeman && levelIndex < config.levels.length - 1;
    const gift = !isDaily && !isForeman && getGift ? getGift(level.id) : null;
    const replay = () => ((isDaily || isForeman) ? applyLevel(level) : loadLevel(levelIndex));
    // «Рекорд: X · Сейчас: Y» — сравнение с собой цепляет сильнее таблиц
    const prevBest = !isDaily && getBestScore ? getBestScore(level.id) : null;
    const recordLine = prevBest > 0
      ? `Рекорд: ${prevBest.toLocaleString('ru-RU')} · Сейчас: ${score.toLocaleString('ru-RU')}${score > prevBest ? ' ▲' : ''}`
      : `${level.subtitle} · Строй выше. Живи лучше. Этажи.`;
    hud.showOverlay({
      mood: 'win',
      ribbon: isForeman ? '🪖 Прораб сдал объект ×2' : isDaily ? '⚡ Башня дня взята' : 'Здание сдано',
      title: level.name,
      sub: recordLine,
      stars: (!isDaily && !isForeman && getRunStars) ? getRunStars() : null,
      stats: [
        { label: 'Очки', value: score.toLocaleString('ru-RU') },
        { label: 'Перфекты', value: perfects },
        { label: 'Макс-комбо', value: maxCombo },
      ],
      gift,
      onClaim: !isDaily && claimGift ? () => claimGift(level.id) : null,
      onShare: onShare ? () => onShare({ level: level.name, floors: tower.count, score }) : null,
      onPhoto: () => enterPhoto(),
      onMap: onMap || null,
      btnText: hasNext ? 'Дальше' : 'Ещё раз',
      onBtn: hasNext ? () => loadLevel(levelIndex + 1) : replay,
      btn2Text: hasNext ? 'Ещё раз' : null,
      onBtn2: hasNext ? () => loadLevel(levelIndex) : null,
    });
  }

  function finishLose() {
    state = 'lose';
    hud.hint(false);
    sfx.fail();
    music.stop(0.8);
    if (haptic) haptic('heavy');
    // Незаконченный забег тоже отправляем: он идёт в общий рейтинг
    if (onLevelComplete && tower.count > 0) {
      onLevelComplete(level.id, { score, floors: tower.count, perfects, taps, daily: !!level.daily, foreman: !!level.foreman });
    }
    // Проигрыш в последней четверти — самый конверсионный момент: до цели
    // рукой подать, кнопка рестарта пульсирует
    const remaining = level.floors - tower.count;
    const almostDone = remaining <= Math.max(3, Math.ceil(level.floors / 4));
    hud.showOverlay({
      mood: 'lose',
      ribbon: 'Стройка заморожена',
      title: 'Блоки кончились',
      pulseBtn: almostDone,
      sub: almostDone
        ? `Оставалось всего ${remaining} ${remaining === 1 ? 'этаж' : remaining < 5 ? 'этажа' : 'этажей'}!`
        : 'Кран ждёт нового прораба. Попробуй ещё раз!',
      stats: [
        { label: 'Очки', value: score.toLocaleString('ru-RU') },
        { label: 'Этажи', value: `${tower.count}/${level.floors}` },
        { label: 'Макс-комбо', value: maxCombo },
      ],
      btnText: 'Ещё раз',
      onBtn: () => (level.daily ? applyLevel(level) : loadLevel(levelIndex)),
    });
  }

  // --- Фото-режим: камера сама обходит сданное здание, HUD спрятан ---
  function enterPhoto() {
    photoMode = true;
    photoAngle = 0;
    hud.hideOverlay();
    hud.setPhotoMode(true);
    tower.lightUp(1);
    crane.group.visible = false; // кран портил бы кадр
  }

  function exitPhoto() {
    photoMode = false;
    hud.setPhotoMode(false);
    crane.group.visible = true;
    showWinOverlay();
  }

  function drop() {
    if (photoMode) return; // тапы по сцене в фото-режиме ничего не роняют
    // Тап во время круга почёта пропускает его — не заставляем ждать
    if (flyby >= 0) {
      flyby = -1;
      tower.lightUp(1);
      crane.group.visible = true;
      showWinOverlay();
      return;
    }
    if (state !== 'swing' || hud.isOverlayOpen || paused) return;
    unlockAudio();
    music.start();               // идемпотентно: реально стартует один раз
    music.ambient(level.theme);  // оттенок эмбиента по теме уровня
    sfx.release();
    // Bullet-time: последний блок уровня падает в замедлении с растянутым свистом
    if (effects.bulletTime && tower.count === level.floors - 1) {
      bulletTime = true;
      sfx.whoosh();
      fovKick.kick(-26); // плавный зум на финальном сбросе
    }
    taps += 1;
    // Скорость — аналитическая производная маятника: детерминированно на любом FPS
    vel.set(swingVx, 0);
    state = 'fall';
    crane.updateRope(0, null, false);
  }

  // Полная загрузка уровня кампании (и рестарт того же уровня)
  function loadLevel(index) {
    levelIndex = Math.max(0, Math.min(index, config.levels.length - 1));
    applyLevel(config.levels[levelIndex]);
  }

  // Башня дня: уровень приходит с сервера, вне кампании (levelIndex не трогаем)
  function loadDaily(dailyLevel) {
    applyLevel(dailyLevel);
  }

  function applyLevel(lvl) {
    music.stop(0.25); // рестарт/смена уровня — музыка стартует заново с первого тапа
    level = lvl;
    P = level.pendulum;
    H = level.blockHeight;
    D = level.blockDepth;

    // Чистим прошлую партию
    if (block) { scene.remove(block); disposeMesh(block); block = null; }
    for (const d of debris) { scene.remove(d.mesh); disposeMesh(d.mesh); }
    debris.length = 0;
    if (spireMesh) { scene.remove(spireMesh); spireMesh = null; }
    tower.reset();
    crane.group.position.y = 0;
    crane.group.visible = true;

    // Пересобираем окружение под тему уровня
    if (env) env.dispose();
    env = applyTheme(scene, lights, level.theme);

    // Призрак рекорда: отметка лучшей высоты прошлых попыток этого уровня.
    // Есть смысл только на непройденном уровне и с рекордом хотя бы 3 этажа.
    if (ghostLine) {
      scene.remove(ghostLine);
      ghostLine.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) { if (o.material.map) o.material.map.dispose(); o.material.dispose(); }
      });
      ghostLine = null;
    }
    // Для Башни дня призрак не показываем: вчерашний «рекорд» — другой уровень
    bestFloors = level.daily ? 0 : (readBest()[level.id] || 0);
    recordBeaten = false;
    if (bestFloors >= 3 && bestFloors < level.floors) {
      ghostLine = makeGhostLine(bestFloors);
      scene.add(ghostLine);
    } else {
      bestFloors = 0; // пройденный уровень: рекорд не показываем и не анонсируем
    }

    // Контактная тень под башней — «приземляет» постройку, стоит дёшево
    if (!contactShadow) {
      contactShadow = createContactShadow(6.5);
      scene.add(contactShadow);
    }
    contactShadow.scale.setScalar(1);

    // Камеру и пружины возвращаем в стартовое состояние без перелёта
    camY.reset(6);
    camZ.reset(camDist);
    fovKick.reset(0);
    squash.reset(1);
    settlingBlock = null;
    shakeAmount = 0;
    flyby = -1;
    milestoneIdx = 0;
    camera.fov = baseFov;
    camera.updateProjectionMatrix();

    // Ежедневный бонус: первый заход в день даёт +1 жизнь на попытку
    maxLives = physics.lives + (getBonusLives ? getBonusLives() : 0);
    lives = maxLives;
    score = 0;
    combo = 0;
    maxCombo = 0;
    perfects = 0;
    shake = 0;
    pullTimer = 0;
    pixelDir = 1;
    taps = 0;
    windValue = 0;
    windTarget = 0;
    windTimer = 0;
    if (onLevelStart) onLevelStart(level.id, !!level.daily, !!level.foreman);
    if (level.foreman) hud.announce('🪖 Режим «Прораб»', 'маятник быстрее, перфект уже, очки ×2');

    // Сброс эффектных состояний прошлого забега
    hitStopTimer = 0;
    bulletTime = false;
    photoMode = false;
    hud.setPhotoMode(false);
    crowd.celebrate(false);
    if (effects.crowd) {
      crowd.setScale(getCrowdScale ? getCrowdScale() : 1);
      crowd.setCount(6); // стартовая кучка зевак; дальше +4 каждые 10 этажей
    } else {
      crowd.setCount(0);
    }

    // Blueprint-интро: чертёж целевого здания, один раз на уровень.
    // Кампания только — Башня дня и «Прораб» стартуют без прелюдий.
    if (effects.blueprint && !level.daily && !level.foreman && !blueprintSeen(level.id)) {
      showBlueprint(level);
    }

    hud.hideOverlay();
    hud.resetScoreInstant();
    hud.setLives(lives, maxLives);
    hud.setFloors(0, level.floors);
    hud.setMeters(0);
    hud.combo(0, false);
    hud.setWind(0, false);
    hud.announce(level.daily ? `⚡ ${level.name}` : `Уровень ${level.id} · ${level.name}`, level.subtitle);

    spawnBlock();
  }

  function updateWind(dt) {
    if (!windActive()) {
      windValue = 0;
      hud.setWind(0, false);
      return;
    }
    const w = level.wind;
    windTimer -= dt;
    if (windTimer <= 0) {
      if (w.gusty) {
        // Порывы рывками: резкая смена направления и силы
        windTimer = 0.8 + Math.random() * 1.4;
        windTarget = (Math.random() * 2 - 1) * w.maxForce;
      } else {
        windTimer = 2 + Math.random() * 2;
        windTarget = (Math.random() * 2 - 1) * w.maxForce;
      }
    }
    const rate = w.gusty ? 6 : 1.2;
    windValue += (windTarget - windValue) * Math.min(1, dt * rate);
    hud.setWind(windValue / w.maxForce, true);
  }

  // --- Кадр ---
  function update(dt, elapsed) {
    if (paused) {
      sfx.creak(false);
      hud.update(dt);
      return; // мир заморожен, рендер продолжается
    }
    // Скрип крана — только пока блок висит на тросе
    sfx.creak(state === 'swing');

    // Hit-stop: мир замирает на 60мс, рендер продолжается — удар «читается»
    if (hitStopTimer > 0) {
      hitStopTimer -= dt;
      hud.update(dt);
      return;
    }
    // Bullet-time: физика последнего падения течёт на 35% скорости
    if (bulletTime && state === 'fall') dt *= 0.35;

    crowd.update(dt, elapsed);
    tower.update(dt);
    fx.update(dt, elapsed);
    if (env) env.update(dt, camera.position.y);
    updateWind(dt);

    const rise = tower.count * H;
    crane.group.position.y += (rise - crane.group.position.y) * Math.min(1, dt * 3);
    crane.group.updateMatrixWorld();
    crane.update(dt, elapsed);

    // Squash & stretch: пружина ведёт масштаб только что севшего блока
    if (settlingBlock) {
      const s = squash.update(dt);
      // Объём сохраняем: сплющивается по Y — расплывается по X/Z
      const lateral = 1 + (1 - s) * 0.6;
      settlingBlock.scale.set(lateral, s, lateral);
      if (Math.abs(s - 1) < 0.004 && Math.abs(squash.velocity) < 0.02) {
        settlingBlock.scale.set(1, 1, 1);
        settlingBlock = null;
      }
    }

    for (let i = debris.length - 1; i >= 0; i--) {
      const d = debris[i];
      d.vy -= physics.gravity * dt;
      d.mesh.position.x += d.vx * dt;
      d.mesh.position.y += d.vy * dt;
      d.mesh.rotation.z += d.spin * dt;
      d.life -= dt;
      d.mesh.material.opacity = Math.min(1, d.life);
      if (d.life <= 0) {
        scene.remove(d.mesh);
        disposeMesh(d.mesh);
        debris.splice(i, 1);
      }
    }

    if (state === 'swing') {
      const speed = swingSpeed();
      swingPhase += speed * dt;
      const p = pivot();
      const k = phys.swingKinematics(P, swingPhase, speed);
      swingVx = k.vx;
      block.position.set(p.x + k.dx, p.y + k.dy, 0);
      block.rotation.z = k.theta * 0.25;
      // Предвкушение: у пика амплитуды блок едва подрагивает — игрок ловит ритм
      const nearPeak = Math.abs(Math.cos(swingPhase));
      if (nearPeak < 0.18) {
        block.position.x += Math.sin(elapsed * 46) * 0.012 * (1 - nearPeak / 0.18);
      }
      crane.updateRope(dt, new THREE.Vector3(block.position.x, block.position.y + H / 2, 0), true);
    } else if (state === 'fall') {
      vel.y -= physics.gravity * dt;
      vel.x += windValue * dt; // снос ветром — ключевая сложность высоток
      block.position.x += vel.x * dt;
      block.position.y += vel.y * dt;
      block.rotation.z *= 0.9;
      const top = tower.topInfo(level.blockWidth);
      if (block.position.y - H / 2 <= top.y) {
        block.position.y = top.y + H / 2;
        block.rotation.z = 0;
        bulletTime = false; // замедление кончается ударом
        resolveLanding();
      }
    } else if (state === 'settle') {
      settleTimer -= dt;
      if (settleTimer <= 0) {
        if (tower.count >= level.floors) {
          finishWin();
        } else {
          spawnBlock();
        }
      }
    } else if (state === 'slip') {
      vel.y -= physics.gravity * dt;
      block.position.x += vel.x * dt;
      block.position.y += vel.y * dt;
      block.rotation.z += slipSpin * dt;
      if (block.position.y < tower.topInfo(level.blockWidth).y - 18) {
        scene.remove(block);
        disposeMesh(block);
        if (lives <= 0) {
          finishLose();
        } else {
          spawnBlock();
        }
      }
    }

    // --- Фото-режим: бесконечная медленная орбита на средней высоте ---
    if (photoMode) {
      photoAngle += dt * 0.35;
      const towerTop = tower.topInfo(level.blockWidth).y;
      const r = camDist * 0.9;
      const bob = Math.sin(photoAngle * 0.6) * towerTop * 0.12; // лёгкое дыхание по высоте
      camera.position.set(Math.sin(photoAngle) * r, 3 + towerTop * 0.55 + bob, Math.cos(photoAngle) * r);
      camera.lookAt(0, towerTop * 0.45, 0);
      // Мир (толпа, фон, частицы) уже обновлён выше — здесь только камера
      hud.update(dt);
      return;
    }

    // --- Круг почёта: камера обходит готовое здание, окна зажигаются волной ---
    if (flyby >= 0) {
      flyby += dt / FLYBY_SECONDS;
      const done = flyby >= 1;
      const p = Math.min(1, flyby);
      const towerTop = tower.topInfo(level.blockWidth).y;

      // Полный оборот с плавным входом и выходом
      const angle = (p * p * (3 - 2 * p)) * Math.PI * 2;
      const r = camDist * 0.85;
      camera.position.set(Math.sin(angle) * r + 8 * Math.cos(angle), 3 + towerTop * (0.25 + p * 0.75), Math.cos(angle) * r);
      camera.lookAt(0, towerTop * (0.35 + p * 0.5), 0);

      // Волна заселения обгоняет камеру на пол-оборота — здание оживает
      tower.lightUp(Math.min(1, p * 1.4));

      // Кран уезжает: он торчал бы из сданного дома и портил кадр
      crane.group.visible = p < 0.12;

      if (done) {
        flyby = -1;
        crane.group.visible = true;
        showWinOverlay();
      }
      hud.update(dt);
      return;
    }

    // --- Камера: пружины вместо lerp, шумовая тряска, FOV-kick ---
    const top = tower.topInfo(level.blockWidth);
    let dist = camDist;
    if (pullTimer > 0) {
      pullTimer -= dt;
      const k = Math.sin(Math.PI * Math.min(1, Math.max(0, 1 - pullTimer / 2.2)));
      dist += k * camDist * 0.7;
    }
    camY.setTarget(top.y + 5.5 + (pullTimer > 0 ? -tower.count * H * 0.25 : 0));
    camZ.setTarget(dist);
    camera.position.y = camY.update(dt);
    camera.position.z = camZ.update(dt);

    // Тряска на шуме: удар читается как удар, а не как качка
    camera.position.x = camBaseX;
    if (shakeAmount > 0.001) {
      shakeAmount *= Math.exp(-6 * dt);
      const s = shakeOffset(shakeAmount, elapsed);
      camera.position.x += s.x;
      camera.position.y += s.y;
    } else {
      shakeAmount = 0;
    }

    const fov = baseFov + fovKick.update(dt);
    if (Math.abs(camera.fov - fov) > 0.01) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }

    const lookY = pullTimer > 0 ? top.y * 0.55 : top.y + 1.5;
    camera.lookAt(-1, lookY, 0);

    // Теневой frustum едет за верхушкой — тени остаются чёткими на любой высоте
    if (lights.sun) followShadow(lights.sun, top.y);
    // Контактная тень живёт у основания, слегка сжимается с ростом башни.
    // На пустой плите её быть не должно — иначе читается как пятно грязи.
    if (contactShadow) {
      const k = Math.max(0.55, 1 - tower.count * 0.006);
      contactShadow.scale.setScalar(k);
      contactShadow.visible = tower.count > 0;
    }

    hud.update(dt);
  }

  loadLevel(startLevel);

  return {
    update,
    drop,
    restart: () => (level && (level.daily || level.foreman) ? applyLevel(level) : loadLevel(levelIndex)),
    loadLevel,
    loadDaily,
    // «Прораб»: тот же уровень с модификаторами из lib/stars.js
    loadForeman(index) {
      levelIndex = Math.max(0, Math.min(index, config.levels.length - 1));
      applyLevel(foremanLevel(config.levels[levelIndex]));
    },
    // Фото-режим: выход и данные для открытки текущей победы
    exitPhoto,
    photoStats: () => ({ level: level.name, floors: tower.count, score }),
    setPaused(v) { paused = v; },
    get paused() { return paused; },
    debug: {
      get state() { return state; },
      get score() { return score; },
      get lives() { return lives; },
      get combo() { return combo; },
      get floors() { return tower.count; },
      get blockX() { return block ? block.position.x : null; },
      get blockY() { return block ? block.position.y : null; },
      get gravity() { return physics.gravity; }, // для предсказания посадки в автотестах
      get blockWidth() { return blockWidth; },
      get vx() { return state === 'swing' ? swingVx : vel.x; },
      get topX() { return tower.topInfo(level.blockWidth).x; },
      get topY() { return tower.topInfo(level.blockWidth).y; },
      get craneY() { return crane.group.position.y; },
      get camPos() { return { x: camera.position.x, y: camera.position.y, z: camera.position.z, fov: camera.fov }; },
      get perfects() { return perfects; },
      get maxCombo() { return maxCombo; },
      get level() { return levelIndex + 1; },
      get levelName() { return level.name; },
      get wind() { return windValue; },
    },
  };
}
